import type * as THREE from 'three';
import { DETECTION } from '../config/tuning';
import type { Terrain } from '../world/Terrain';

/**
 * The detection model (SPEC §3.5.3).
 *
 * Detection is not a sphere. It is the conjunction of four independent tests, and each one is a
 * separate pure function here so that each can be unit-tested on its own and — more importantly —
 * so the debug view can show the player *which* of the four is currently saving them. A single
 * `canSee()` returning a boolean would be untunable and unteachable.
 *
 *   1. Radar horizon — the earth curves, so altitude buys detection range and giving it up buys
 *      invisibility. Mechanical, not cosmetic.
 *   2. Terrain line of sight — a ray march against the heightfield.
 *   3. Range, scaled by aspect — a nose-on target is a smaller return than a beam-on one.
 *   4. Doppler notch — the single most important mechanic in the game (SPEC §3.5.3). Flying
 *      perpendicular to a radar puts the return inside the ground-clutter filter and makes the
 *      aircraft nearly invisible to it.
 *
 * Units: metres, m/s, radians. Nothing here allocates.
 */

/** What a radar needs to know about what it is looking at. */
export interface RadarTarget {
  positionM: THREE.Vector3;
  velocityMs: THREE.Vector3;
  /** Unit vector along the airframe's nose, world space. */
  forward: THREE.Vector3;
  rcsM2: number;
  /**
   * Height above the surface beneath the target, metres. Engagement envelopes are quoted against
   * this rather than against sea level, because the whole point of a minimum altitude is that
   * flying *below* it — down among the terrain — puts the aircraft out of the envelope.
   */
  altitudeAglM: number;
}

/** The four tests, kept separately so the debug view can show which one is doing the work. */
export interface DetectionTests {
  withinHorizon: boolean;
  hasLineOfSight: boolean;
  withinRange: boolean;
  /** 0 fully notched, 1 fully visible to Doppler. */
  dopplerVisibility: number;
  /** All four passed. */
  detected: boolean;

  // --- Diagnostics, for the debug view and the RWR
  rangeM: number;
  horizonM: number;
  effectiveRangeM: number;
  aspectFactor: number;
  radialVelocityMs: number;
}

export function createDetectionTests(): DetectionTests {
  return {
    withinHorizon: false,
    hasLineOfSight: false,
    withinRange: false,
    dopplerVisibility: 0,
    detected: false,
    rangeM: 0,
    horizonM: 0,
    effectiveRangeM: 0,
    aspectFactor: 1,
    radialVelocityMs: 0,
  };
}

/**
 * Radar horizon between two heights above sea level, metres.
 *
 * `d_km = 4.12·(√h1_m + √h2_m)` — the standard 4/3-earth approximation, quoted in kilometres,
 * returned here in metres because everything else in the simulation is metres. Against a 6 m
 * mast, a drone at 30 m is invisible beyond about 32 km; at 300 m it is visible at 81 km. That
 * ratio is the reason low flight is worth its fuel.
 *
 * Heights below sea level clamp to zero: a radar cannot see further by being in a hole.
 */
export function radarHorizonM(radarHeightM: number, targetHeightM: number): number {
  const h1 = radarHeightM > 0 ? radarHeightM : 0;
  const h2 = targetHeightM > 0 ? targetHeightM : 0;
  return DETECTION.horizonK * 1000 * (Math.sqrt(h1) + Math.sqrt(h2));
}

/**
 * True when terrain does not block the line from the radar to the target.
 *
 * A thin wrapper on `Terrain.lineOfSight` so that the detection model has one name for the test
 * and the caching below has one thing to cache. Endpoints are excluded by the terrain march — a
 * radar sits on the ground, and including its own sample would report every site as blind.
 */
export function hasLineOfSight(
  terrain: Terrain,
  radarPositionM: THREE.Vector3,
  targetPositionM: THREE.Vector3,
): boolean {
  return terrain.lineOfSight(radarPositionM, targetPositionM);
}

/**
 * Radar cross-section multiplier from viewing aspect, unitless.
 *
 * 0.6 nose-on or tail-on, 1.0 beam-on (SPEC §3.5.3), interpolated by |cos| of the angle between
 * the airframe's nose and the line to the radar. Both arguments must be unit vectors.
 *
 * Note this is symmetric front-to-back: a radar cannot tell a nose-on target from a tail-on one
 * by cross-section alone, and pretending otherwise would hand the player a free escape.
 */
export function rcsAspectFactor(
  targetForwardUnit: THREE.Vector3,
  targetToRadarUnit: THREE.Vector3,
): number {
  const alignment = Math.abs(targetForwardUnit.dot(targetToRadarUnit));
  const clamped = alignment > 1 ? 1 : alignment < 0 ? 0 : alignment;
  return DETECTION.rcsAspectBeam + (DETECTION.rcsAspectNose - DETECTION.rcsAspectBeam) * clamped;
}

/**
 * Detection range against a given aspect, metres.
 *
 * The aspect factor scales the range directly rather than through the fourth root the radar
 * equation would demand. That is deliberate: the point of this term is to make *aspect* legible
 * as a tactic, and a physically exact version would move the detection range by about 12% between
 * nose-on and beam-on, which no player would ever notice.
 */
export function rangeWithAspect(baseRangeM: number, aspectFactor: number): number {
  return baseRangeM * aspectFactor;
}

/**
 * Component of the target's velocity along the line to the radar, m/s. Positive is opening.
 *
 * This is the number the notch is about. `radarPositionM − targetPositionM` is the direction the
 * radar lies in; the velocity's projection onto it is what a pulse-Doppler receiver measures.
 */
export function radialVelocityMs(
  velocityMs: THREE.Vector3,
  targetPositionM: THREE.Vector3,
  radarPositionM: THREE.Vector3,
): number {
  const dx = radarPositionM.x - targetPositionM.x;
  const dy = radarPositionM.y - targetPositionM.y;
  const dz = radarPositionM.z - targetPositionM.z;
  const length = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (length < 1e-6) return 0;
  return -(velocityMs.x * dx + velocityMs.y * dy + velocityMs.z * dz) / length;
}

/**
 * Visibility to a pulse-Doppler radar, 0 (fully notched) to 1 (fully visible).
 *
 * Ground radar rejects returns whose radial velocity falls inside the ground-clutter notch. The
 * transition is a soft band rather than a step, and that softness is a game-design decision, not
 * a physical one: a hard edge would be unfindable by feel, whereas a band lets the player hunt
 * for the notch by watching the RWR fade and hold the heading that keeps it faded.
 *
 * A `notchVelocityMs` of zero means the threat has no Doppler processing at all — an IR MANPADS —
 * and is always visible. Notching it does nothing, which is the lesson that threat exists to teach.
 */
export function dopplerVisible(
  radialVelocityMs: number,
  notchVelocityMs: number = DETECTION.notchVelocityMs,
  softnessMs: number = DETECTION.notchSoftnessMs,
): number {
  if (notchVelocityMs <= 0) return 1;
  const speed = Math.abs(radialVelocityMs);
  const low = notchVelocityMs - softnessMs;
  const high = notchVelocityMs + softnessMs;
  if (speed <= low) return 0;
  if (speed >= high) return 1;
  const t = (speed - low) / (high - low);
  // Smoothstep, so the edges of the band are flat and the middle is where the feel lives.
  return t * t * (3 - 2 * t);
}

/**
 * Run all four tests and write the result into `out`.
 *
 * `baseRangeM` is the search or track range depending on what the caller is asking about — a site
 * uses its search range to wake up and its track range to hold a lock, and they are different
 * numbers for the same site.
 */
export function evaluateDetection(
  terrain: Terrain,
  radarPositionM: THREE.Vector3,
  target: RadarTarget,
  baseRangeM: number,
  notchVelocityMs: number,
  out: DetectionTests,
  lineOfSightResult?: boolean,
): DetectionTests {
  const dx = target.positionM.x - radarPositionM.x;
  const dy = target.positionM.y - radarPositionM.y;
  const dz = target.positionM.z - radarPositionM.z;
  out.rangeM = Math.sqrt(dx * dx + dy * dy + dz * dz);

  // 1. Radar horizon. `radarPositionM.y` is the antenna height — ground plus mast — which the
  // site works out once at placement rather than every evaluation.
  out.horizonM = radarHorizonM(radarPositionM.y, target.positionM.y);
  out.withinHorizon = out.rangeM <= out.horizonM;

  // 2. Terrain line of sight — supplied by the caller when it has a cached answer.
  out.hasLineOfSight =
    lineOfSightResult ?? hasLineOfSight(terrain, radarPositionM, target.positionM);

  // 3. Range against the aspect-scaled detection range. The direction from the target toward the
  // radar is the negation of the offset computed above; only its alignment with the nose matters,
  // and |cos| is sign-blind, so the negation cancels.
  const inverseRange = out.rangeM > 1e-6 ? 1 / out.rangeM : 0;
  const alignment = Math.abs(
    (target.forward.x * dx + target.forward.y * dy + target.forward.z * dz) * inverseRange,
  );
  const clampedAlignment = alignment > 1 ? 1 : alignment;
  out.aspectFactor =
    DETECTION.rcsAspectBeam +
    (DETECTION.rcsAspectNose - DETECTION.rcsAspectBeam) * clampedAlignment;
  out.effectiveRangeM = rangeWithAspect(baseRangeM, out.aspectFactor);
  out.withinRange = out.rangeM <= out.effectiveRangeM;

  // 4. Doppler notch.
  out.radialVelocityMs = radialVelocityMs(target.velocityMs, target.positionM, radarPositionM);
  out.dopplerVisibility = dopplerVisible(out.radialVelocityMs, notchVelocityMs);

  out.detected =
    out.withinHorizon &&
    out.hasLineOfSight &&
    out.withinRange &&
    out.dopplerVisibility >= DETECTION.notchDetectThreshold;

  return out;
}

/**
 * One-second line-of-sight cache, per SPEC §3.5.3.
 *
 * The ray march is the expensive test — 64 heightfield samples — and the answer barely changes
 * while the aircraft crosses a single terrain cell. The key is the quantised target position, so
 * the cache invalidates when the aircraft has actually moved somewhere new rather than on a timer
 * alone. At 90 m/s a 120 m cell is re-marched a little over once a second regardless.
 */
export class LineOfSightCache {
  private cellX = Number.NaN;
  private cellZ = Number.NaN;
  private cellY = Number.NaN;
  private expiresAtS = -1;
  private result = false;

  /** Cached `hasLineOfSight`, re-marching only when the target has moved or the entry aged out. */
  query(
    terrain: Terrain,
    radarPositionM: THREE.Vector3,
    targetPositionM: THREE.Vector3,
    nowS: number,
  ): boolean {
    const cell = DETECTION.losCacheCellM;
    const x = Math.round(targetPositionM.x / cell);
    const z = Math.round(targetPositionM.z / cell);
    const y = Math.round(targetPositionM.y / cell);

    if (x === this.cellX && z === this.cellZ && y === this.cellY && nowS < this.expiresAtS) {
      return this.result;
    }

    this.cellX = x;
    this.cellZ = z;
    this.cellY = y;
    this.expiresAtS = nowS + DETECTION.losCacheS;
    this.result = hasLineOfSight(terrain, radarPositionM, targetPositionM);
    return this.result;
  }

  /** Drop the cached answer — used when a site moves or the simulation resets. */
  invalidate(): void {
    this.expiresAtS = -1;
    this.cellX = Number.NaN;
  }
}
